// lib
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import {
  questionPositionToId,
  getFilteredQuestions,
  getSentimentsQuestions
} from '@aytm/app/stats/page/comments/lib/utils'

import { itemTemplate } from '@aytm/app/stats/page/components/lib/templates'

// components
import { MultiSelect } from 'primereact/multiselect'

// styles
import style from '@aytm/app/stats/page/sidebar/coding/styles/CodingPanel.module.scss'

let showStatusTimer = null

const Sentiments = ({ questions, sentiments, features, onSentimentsChange }) => {
  const [sentimentsValue, setSentimentsValue] = useState([])

  const sentimentsRef = useRef(null)

  const showStatusRef = useRef(null)

  const filteredQuestions = useMemo(() => getFilteredQuestions(questions), [questions])

  const sentimentsQuestions = useMemo(() => getSentimentsQuestions(filteredQuestions), [filteredQuestions])

  const sentimentsOptions = useMemo(() => {
    let value = []

    const options = filteredQuestions.reduce((acc, cur) => {
      const id = (0 < cur.position && questionPositionToId(cur.position, { questions: filteredQuestions })) || cur.id
      const key = (0 < cur.position && 'q' + cur.position) || cur.id

      if (0 < cur.batches?.length) {
        let _acc = []
        let _value = []

        if ('hm' == cur.type) {
          for (const b of cur.batches) {
            if (!b.answerVariants.some(av => av.comments)) continue

            _acc.push({
              key: `${key}.b${b.position}`,
              text: `<b>${id.toUpperCase()}SQ${b.position}:</b> ${b.text}`,
              lvl: 1
            })

            if (sentiments[`${key}.b${b.position}`] != false && sentiments[key] != false) {
              _value.push(`${key}.b${b.position}`)
            }
          }

          if (0 < _acc.length) {
            acc.push({
              key,
              text: `<b>${id.toUpperCase()}:</b> ${cur.text}`,
              lvl: 0,
              disabled: 1 >= _acc.length
            })

            if (sentiments[key] != false && sentiments[`${key}.a1`] != false && 1 < _acc.length && 0 < _value.length) {
              value.push(key)
            }

            if (
              sentiments[key] != false &&
              sentiments[`${key}.a1`] != false &&
              (1 >= _acc.length || 0 < _value.length)
            ) {
              value = value.concat(_value)
              return acc.concat(_acc)
            }
          }
        } else {
          let count = 0

          for (const b of cur.batches) {
            const __acc = []
            const __value = []

            for (const av of b.answerVariants) {
              if (!av.comments) continue

              __acc.push({
                key: `${key}.b${b.position}.a${av.position}`,
                text: `<b>${id.toUpperCase()}SQ${b.position}A${av.position}:</b> ${av.text}`,
                lvl: 2
              })

              if (
                sentiments[`${key}.b${b.position}.a${av.position}`] != false &&
                sentiments[`${key}.b${b.position}`] != false &&
                sentiments[key] != false
              ) {
                __value.push(`${key}.b${b.position}.a${av.position}`)
              }
            }
            if (0 < __acc.length) {
              _acc.push({
                key: `${key}.b${b.position}`,
                text: `<b>${id.toUpperCase()}SQ${b.position}:</b> ${b.text}`,
                lvl: 1,
                disabled: 1 >= __acc.length
              })

              if (
                sentiments[`${key}.b${b.position}`] != false &&
                sentiments[key] != false &&
                1 < __acc.length &&
                0 < __value.length
              ) {
                _value.push(`${key}.b${b.position}`)
              }

              if (
                sentiments[`${key}.b${b.position}`] != false &&
                sentiments[key] != false &&
                (1 >= __acc.length || 0 < __value.length)
              ) {
                _acc = _acc.concat(__acc)
                _value = _value.concat(__value)
              }

              if (1 < __acc.length) count++
            }
          }
          if (0 < _acc.length) {
            acc.push({
              key,
              text: `<b>${id.toUpperCase()}:</b> ${cur.text}`,
              lvl: 0,
              disabled: 1 >= count
            })

            if (sentiments[key] != false && 1 < count && 0 < _value.length) {
              value.push(key)
            }

            if (sentiments[key] != false && (1 >= count || 0 < _value.length)) {
              value = value.concat(_value)
              return acc.concat(_acc)
            }
          }
        }
      } else if (0 < cur.answerVariants?.length && !['va'].includes(cur.type)) {
        const _acc = []
        const _value = []

        if (['cx', 'scx'].includes(cur.type)) {
          const keys = Object.keys(sentimentsQuestions[key])
          const usedKeys = []

          const checkAnswerVariants = (_id, _key, answerVariants, p, i = 0) => {
            if (!Array.isArray(answerVariants) || !p[i]) return

            const av = answerVariants[Number(p[i].slice(1)) - 1]
            if (null === av) return

            const __id = `${_id}${p[i]}`
            const __key = `${_key}.${p[i]}`

            if (0 > usedKeys.indexOf(__key)) {
              _acc.push({
                key: __key,
                text: `<b>${__id.toUpperCase()}:</b> ${av.text}`,
                lvl: Math.min(i + 1, 2),
                disabled: !av.comments
              })

              if (sentiments[__key] != false && sentiments[key] != false && av.comments) {
                _value.push(__key)
              }

              usedKeys.push(__key)
            }

            checkAnswerVariants(__id, __key, av.answerVariants, p, i + 1)
          }

          for (const k of keys) {
            const p = k.split('.').slice(1)

            checkAnswerVariants(id, key, cur.answerVariants, p)
          }
          if (0 < _acc.length) {
            acc.push({
              key,
              text: `<b>${id.toUpperCase()}:</b> ${cur.text}`,
              lvl: 0,
              disabled: 1 >= keys.length
            })

            if (sentiments[key] != false && 1 < keys.length && 0 < _value.length) {
              value.push(key)
            }

            if (sentiments[key] != false && (1 >= keys.length || 0 < _value.length)) {
              value = value.concat(_value)
              return acc.concat(_acc)
            }
          }
        } else {
          for (const av of cur.answerVariants) {
            if ('oex' != cur.type && !av.comments) continue

            _acc.push({
              key: `${key}.a${av.position}`,
              text: `<b>${id.toUpperCase()}A${av.position}:</b> ${av.text}`,
              lvl: 1
            })

            if (sentiments[`${key}.a${av.position}`] != false && sentiments[key] != false) {
              _value.push(`${key}.a${av.position}`)
            }
          }
          if (0 < _acc.length) {
            acc.push({
              key,
              text: `<b>${id.toUpperCase()}:</b> ${cur.text}`,
              lvl: 0,
              disabled: 1 >= _acc.length
            })

            if (sentiments[key] != false && 1 < _acc.length && 0 < _value.length) {
              value.push(key)
            }

            if (sentiments[key] != false && (1 >= _acc.length || 0 < _value.length)) {
              value = value.concat(_value)
              return acc.concat(_acc)
            }
          }
        }
      } else {
        acc.push({ key, text: `<b>${id.toUpperCase()}:</b> ${cur.text}`, lvl: 0 })

        if (sentiments[key] != false) value.push(key)
      }
      return acc
    }, [])

    setSentimentsValue(value)

    return options
  }, [sentiments, filteredQuestions, sentimentsQuestions])

  const onChange = useCallback(
    event => {
      const values = sentimentsOptions
        .filter(option => !event.value.includes(option.key) && !option.disabled)
        .map(option => option.key)
        .reduceRight((acc, cur) => {
          const path = cur.split('.')
          const question = filteredQuestions.find(q => `q${q.position}` == path[0])

          let keys = []
          if (['cx', 'scx'].includes(question.type)) {
            keys = `q${question.position}` == cur ? Object.keys(sentimentsQuestions[cur]) : [cur]
          } else {
            const obj = path.reduce((_acc, _cur, i) => _acc[path.slice(0, i + 1).join('.')], sentimentsQuestions)

            const findKeys = (_key, _obj = {}) => {
              let _keys = []
              if (0 < Object.keys(_obj).length) {
                for (const [k, attr] of Object.entries(_obj)) {
                  _keys = _keys.concat(findKeys(k, attr))
                }
              } else _keys.push(_key)
              return _keys
            }
            keys = (obj && 0 < Object.keys(obj).length && findKeys(cur, obj)) || [cur]
          }

          const value = event.value.includes(cur)
          for (const key of keys) acc[key] = value

          return acc
        }, {})

      onSentimentsChange(values)
    },
    [filteredQuestions, sentimentsQuestions, sentimentsOptions, onSentimentsChange]
  )

  useEffect(() => {
    if (showStatusRef.current) {
      showStatusTimer && clearTimeout(showStatusTimer)
      sentimentsRef.current.show()
    }
  }, [sentimentsOptions])

  return (
    <div className={`${style.row} ${style['row-sentiments']} p-fluid`}>
      <div className="p-field p-col-12">
        <MultiSelect
          ref={sentimentsRef}
          className="p-multiselect-rounded p-multiselect-reversed"
          placeholder="Sentiment manual ON/OFF"
          value={sentimentsValue}
          options={sentimentsOptions}
          optionValue="key"
          optionLabel="text"
          itemTemplate={itemTemplate}
          buttonLabelTemplate={() => 'Sentiment manual ON/OFF'}
          fixedDropdownDir="bottom"
          showSelectAll={false}
          disabled={features.oe_sentiments_management.disabled}
          onChange={features.oe_sentiments_management.disabled ? null : onChange}
          onShow={() => (showStatusRef.current = true)}
          onHide={() => (showStatusTimer = setTimeout(() => (showStatusRef.current = null), 300))}
        />
      </div>
    </div>
  )
}

Sentiments.propTypes = {
  questions: PropTypes.array,
  sentiments: PropTypes.object,
  features: PropTypes.object,
  onSentimentsChange: PropTypes.func
}

Sentiments.displayName = 'Sentiments'

export default Sentiments
